import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import './index.css'
import '@/lib/sentry'
import '@/lib/posthog'

const isRetryableQueryError = (error: unknown) => {
  const maybeError = error as { status?: number; statusCode?: number; code?: string; message?: string }

  // Check HTTP status (fetch errors, Supabase HTTP responses)
  const status = maybeError?.status ?? maybeError?.statusCode
  if (typeof status === 'number') {
    if (status >= 400 && status < 500) return false
  }

  // PostgREST error codes (e.g. PGRST116, PGRST204)
  if (typeof maybeError?.code === 'string' && maybeError.code.startsWith('PGRST')) {
    return false
  }

  // Postgres error codes (5-char alphanumeric, e.g. "42703" for undefined column)
  if (typeof maybeError?.code === 'string' && /^[0-9A-Z]{5}$/.test(maybeError.code)) {
    return false
  }

  // Supabase client errors that indicate bad requests (not transient)
  if (typeof maybeError?.message === 'string') {
    const msg = maybeError.message.toLowerCase()
    if (msg.includes('not found') || msg.includes('does not exist') || msg.includes('bad request') || msg.includes('permission denied')) {
      return false
    }
  }

  return true
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (!isRetryableQueryError(error)) return false
        return failureCount < 1
      },
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
